import {
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    QueryResult,
    useSuspenseQuery,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type {
    PaymentAgreementId,
    ApprovalResult,
    AvtalegiroAgreement,
    AvtalegiroProposal,
    AvtalegiroAgreementDetails,
    MoveExternalAvtalegiros,
    MoveExternalAvtalegirosResult,
} from '../generated-types';
import {
    deleteAvtalegiroProposal,
    deleteAvtaleGiroAgreement,
    getAvtaleGiro,
    getAvtaleGiroDetails,
    getAvtaleGiroProposals,
    listExternalAvtalegiros,
    moveAvtalegiros,
} from '../services/avtaleGiroService';

export const AVTALEGIRO_PROPOSAL_KEY = ['avtalegiroproposals'];

export const useAvtalegiro = (enabled = true): QueryResult<AvtalegiroAgreement[]> =>
    useQuery<AvtalegiroAgreement[]>({
        queryKey: ['avtalegiros'],
        queryFn: getAvtaleGiro,
        enabled,
    });

export const useSuspendedAvtaleGiroProposals = () =>
    useSuspenseQuery<AvtalegiroProposal[], RequestExceptionWithResponse>({
        queryKey: AVTALEGIRO_PROPOSAL_KEY,
        queryFn: getAvtaleGiroProposals,
    });

export const useAvtaleGiroProposals = (enabled: boolean): QueryResult<AvtalegiroProposal[]> =>
    useQuery<AvtalegiroProposal[]>({
        queryKey: ['avtalegiroproposals'],
        queryFn: getAvtaleGiroProposals,
        enabled,
    });

export const useAvtaleGiroProposalDelete = (): UseMutationResult<
    ApprovalResult,
    RequestExceptionWithResponse,
    PaymentAgreementId
> => {
    return useMutation<ApprovalResult, RequestExceptionWithResponse, PaymentAgreementId>({
        mutationKey: ['delete-avtalegiro-proposal'],
        mutationFn: deleteAvtalegiroProposal,
    });
};

export const useRefreshAvtaleGiroProposals = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ['avtalegiroproposals'] });
};

export const useRefreshAvtalegiro = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ['avtalegiros'] });
};

export const useAvtalegiroDetails = (id?: PaymentAgreementId): QueryResult<AvtalegiroAgreementDetails> => {
    const queryClient = useQueryClient();

    const queryKey = ['avtalegiro-details', id];
    const queryState = useQuery<AvtalegiroAgreementDetails>({
        queryKey,
        queryFn: () => getAvtaleGiroDetails(id!),
        enabled: !!id,
    });

    return {
        ...queryState,
        invalidateQuery: () => queryClient.invalidateQueries({ queryKey, refetchType: 'none' }),
    };
};

export const useDeleteAvtalegiro = () =>
    useMutation<ApprovalResult, RequestExceptionWithResponse, PaymentAgreementId>({
        mutationKey: ['avtalegiro'],
        mutationFn: deleteAvtaleGiroAgreement,
    });

export const useListExternalAvtalegiros = (dynamicLinkId: string | undefined, enabled: boolean) =>
    useQuery({
        queryKey: ['externalAvtalegiro', dynamicLinkId],
        queryFn: () => listExternalAvtalegiros({ value: dynamicLinkId! }),
        enabled: enabled && Boolean(dynamicLinkId),
    });

export const useMutateMoveAvtalegiros = (
    dynamicLinkId: string | undefined,
): UseMutationResult<MoveExternalAvtalegirosResult[], RequestExceptionWithResponse, MoveExternalAvtalegiros> => {
    return useMutation<MoveExternalAvtalegirosResult[], RequestExceptionWithResponse, MoveExternalAvtalegiros>({
        mutationKey: ['move-external-avtalegiro', dynamicLinkId],
        mutationFn: moveAvtalegiros({ value: dynamicLinkId! }),
    });
};
